import { Space } from "antd";
import axios from "axios";
import PrimaryButton from "components/Buttons/PrimaryButton/PrimaryButton";
import CustomDrawer from "components/CustomDrawer/CustomDrawer";
import { FC, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { BOOLEAN_OPTIONS } from "utils/constants/selectorOptions";
import { Typography, Divider } from "antd";
import './AskAi.css'
const { Title, Paragraph, Text } = Typography;

interface AskAIProps {
  showAI: boolean;
  setShowAI: React.Dispatch<React.SetStateAction<boolean>>;
}

const AskAI: FC<AskAIProps> = ({ setShowAI, showAI }) => {
  const campaignData = useSelector((state: any) => state.campaignData.data);
  const [loading, setLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState<any>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [error,setError]=useState(false)

  const askAiForSuggestion = async () => {
    try {
      setLoading(true);
      setError(false)
      setAiResponse(null);
      const data = {
        payload:
          "This is a Push campaign for a retail banking app offering cashback and rewards on card spends, loans, term deposits and other banking products. Analyze the campaign and provide 3 improvements related to creative messaging, timing, and personalization. Suggest alternate headlines and segmentation ideas as well.",
        context: [
          {
            title: "💳 Unlock your Weekend Cashback!",
            body: "Spend ₹1000 or more with your Debit Card this weekend and get 10% cashback instantly. Offer valid till Sunday midnight!",
          },
          {
            title: "🎉 Celebrate with Rewards!",
            body: "Earn double reward points on all festive shopping using your BankNow credit card. Swipe now & redeem instantly!",
          },
          {
            title: "💼 Need Extra Funds?",
            body: "Get an instant personal loan of up to ₹5 Lakhs at just 10.99% interest. No paperwork, no waiting! Apply in minutes.",
          },
          {
            title: "📲 Loan in 2 Taps!",
            body: "You're pre-approved for a personal loan. Get funds credited instantly to your account. Tap to check your offer.",
          },
          {
            title: "📈 Secure Your Savings!",
            body: "Earn up to 7.5% interest on a 12-month term deposit. Start your FD online with just ₹5,000!",
          },
          {
            title: "💡 Smarter Savings Start Here",
            body: "Open a tax-saving Fixed Deposit & enjoy assured returns with tax benefits under 80C. Limited-time interest rate!",
          },
          {
            title: "🏡 Your Dream Home Awaits!",
            body: "Get home loans at 8.25% with zero processing fees. Flexible EMI options. Apply now!",
          },
          {
            title: "🔑 Unlock Home Loan Benefits",
            body: "Pre-approved home loan offers with instant digital approval. Tap to check your eligibility.",
          },
          {
            title: "🚘 Drive Home Happiness",
            body: "Low interest auto loans starting at just 7.99%! Pre-approved offer inside – tap to view details.",
          },
          {
            title: "🚗 Your Dream Car, Our Easy Loan",
            body: "Finance your new car with 100% on-road price coverage. No paperwork required!",
          },
          {
            title: "✨ Say Hello to Premium!",
            body: "Get lifetime-free access to our Platinum Credit Card with airport lounge access & rewards on every swipe.",
          },
          {
            title: "🎁 Your Welcome Gift Awaits!",
            body: "Activate your new card and get an Amazon voucher worth ₹500. Spend ₹1000 in first 7 days to claim.",
          },
          {
            title: "🧮 Simplify Your EMIs",
            body: "Transfer your credit card balance to BankNow & enjoy 0% interest for 3 months. Save big now!",
          },
          {
            title: "📉 Bring Down Your Debt",
            body: "Convert large purchases into easy EMIs with just a tap. Check eligible transactions today!",
          },
          {
            ...campaignData,
            segmentinfo: undefined,
          },
        ],
      };
      const response = await axios.post(
        "https://motaai.appice.io/api/v1/campaign_assistant",
        data
      );
      setAiResponse(response?.data || {});
    } catch (error) {
      setError(true)
      setAiResponse({
        suggestions: [],
        warnings: [],
        notes: "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showAI) {
      askAiForSuggestion();
    }
  }, [showAI]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [aiResponse]);

  return (
    <CustomDrawer
      size="large"
      open={showAI}
      placement="right"
      onClose={() => setShowAI(false)}
      loading={loading}
      className="p-0 ask-ai-drawer-container"
      closable={BOOLEAN_OPTIONS.FALSE}
      title="AI Assistant"
      footer={
        <Space className="d-flex justify-content-end">
          <PrimaryButton
            className="btn-close-ai"
            onClick={() => setShowAI(false)}
          >
            Close
          </PrimaryButton>
        </Space>
      }
    >
      <div className="ai-drawer-body">
        <Typography>
          {error?<span>Something went wrong while contacting the AI assistant. <PrimaryButton type="link" onClick={askAiForSuggestion}>
            Try again
          </PrimaryButton></span>:<></>}
          {aiResponse?.suggestions?.length > 0 && (
            <>
              <Title level={4}>💡 Suggestions</Title>
              {aiResponse.suggestions.map((item: string, index: number) => (
                <Paragraph key={index} className="ai-response-paragraph">
                  <Text>• {item}</Text>
                </Paragraph>
              ))}
              <Divider />
            </>
          )}

          {aiResponse?.warnings?.length > 0 && (
            <>
              <Title level={4}>⚠️ Warnings</Title>
              {aiResponse.warnings.map((item: string, index: number) => (
                <Paragraph key={index} className="ai-response-paragraph">
                  <Text type="danger">• {item}</Text>
                </Paragraph>
              ))}
              <Divider />
            </>
          )}

          {aiResponse?.improved_creatives?.length > 0 && (
            <>
              <Title level={4}>🎨 Improved Creatives</Title>
              {aiResponse.improved_creatives.map(
                (creative: { title: string; body: string }, index: number) => (
                  <div key={index} className="ai-creative-item">
                    <Text strong>{creative.title}</Text>
                    <Paragraph>{creative.body}</Paragraph>
                  </div>
                )
              )}
              <Divider />
            </>
          )}

          {aiResponse?.recommended_segments?.length > 0 && (
            <>
              <Title level={4}>📊 Recommended Segments</Title>
              <ul className="ai-segment-list">
                {aiResponse.recommended_segments.map(
                  (seg: string, index: number) => (
                    <li key={index}>
                      <Text>{seg}</Text>
                    </li>
                  )
                )}
              </ul>
              <Divider />
            </>
          )}

          {aiResponse?.notes && (
            <>
              <Title level={4}>📝 Notes</Title>
              <Paragraph>{aiResponse.notes}</Paragraph>
            </>
          )}
        </Typography>

        <div ref={bottomRef} />
      </div>
    </CustomDrawer>
  );
};

export default AskAI;
